import { TransferType } from '@n2-common/serverGeneratedTypings/transferType';
import { TFunction } from 'i18next';
import { getI18n } from 'react-i18next';
import * as Yup from 'yup';

export const accountNumberForMyPage = (t: TFunction, transferType: TransferType) => accountnumber(t, 'InvalidAccountNumber', transferType);
export const accountNumberForApplicationProcess = (t: TFunction) => accountnumber(t, 'validation:InvalidAccountNumber', TransferType.Normal);

type ValidationScope = 'all' | 'normal';

/**
 * @param scope Config which accountnumber types should be accepted. 'all' - both BG/PG and normal. 'normal' - just normal numbers.
 * @returns 
 */
export function getAccountnumberValidation(scope: ValidationScope) {
    const { t } = getI18n();
    return Yup.string()
        .typeError(t<string>('validation:AccountNumberTypeError'))
        .required(t<string>('validation:AccountNumberRequired'))
        .test('test-swedishaccountnumber', t<string>('validation:InvalidAccountNumber'), (value) => {
            return parseSwedishAccountNumber(value, scope).isValid;
        });
}

export const cleanupSwedishGiroAccountNumber = (value: string) => value.replace('-', '');
export const cleanupTransferAccountNumber = (value: string) => value.replace(/\D/g, '');

const accountnumber = (t: TFunction, validationTranslationKey: string, transferType: TransferType) =>
    Yup.string()
        .typeError(t<string>('validation:AccountNumberTypeError'))
        .required(t<string>('validation:AccountNumberRequired'))
        .test(
            'test-swedishaccountnumber', // this is used internally by yup
            t<string>(validationTranslationKey), //validation message
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            (value) => {
                if (!value) {
                    return false;
                }
                if (transferType === TransferType.PlusGiro) {
                    return /^[0-9]{7}[0-9]{1}$/.test(cleanupSwedishGiroAccountNumber(value));
                }
                if (transferType === TransferType.BankGiro) {
                    return /^[0-9]{3,4}[0-9]{4}$/.test(cleanupSwedishGiroAccountNumber(value));
                }
                const res = validateSwedishAccountNumber(value);
                return res !== false && res.accountNumber !== null;
            }
        ); // return true false based on validation

interface Bank {
    name: string;
    type: number;
    comment: number;
    regex: RegExp;
}

function validateSwedishAccountNumber(rawNumber: string) {
    if (typeof rawNumber !== 'string') {
        console.error('Input accout number must be a string');
        return false;
    }
    const n = cleanupTransferAccountNumber(rawNumber);
    const banks: Bank[] = [
        {
            name: 'Amfa Bank',
            type: 1,
            comment: 2,
            regex: /^966[0-9]{8}$/,
        },
        {
            name: 'Avanza Bank',
            type: 1,
            comment: 2,
            regex: /^95[5-6][0-9]{8}$/,
        },
        {
            name: 'BlueStep Finans',
            type: 1,
            comment: 1,
            regex: /^968[0-9]{8}$/,
        },
        {
            name: 'BNP Paribas SA.',
            type: 1,
            comment: 2,
            regex: /^947[0-9]{8}$/,
        },
        {
            name: 'Citibank',
            type: 1,
            comment: 2,
            regex: /^904[0-9]{8}$/,
        },
        {
            name: 'Danske Bank',
            type: 1,
            comment: 1,
            regex: /^(12|13|24)[0-9]{9}$/,
        },
        {
            name: 'DNB Bank',
            type: 1,
            comment: 2,
            regex: /^(919|926)[0-9]{8}$/,
        },
        {
            name: 'Ekobanken',
            type: 1,
            comment: 2,
            regex: /^970[0-9]{8}$/,
        },
        {
            name: 'Erik Penser',
            type: 1,
            comment: 2,
            regex: /^959[0-9]{8}$/,
        },
        {
            name: 'Forex Bank',
            type: 1,
            comment: 1,
            regex: /^94[0-4][0-9]{8}$/,
        },
        {
            name: 'ICA Banken',
            type: 1,
            comment: 1,
            regex: /^927[0-9]{8}$/,
        },
        {
            name: 'IKANO Bank',
            type: 1,
            comment: 1,
            regex: /^917[0-9]{8}$/,
        },
        {
            name: 'JAK Medlemsbank',
            type: 1,
            comment: 2,
            regex: /^967[0-9]{8}$/,
        },
        {
            name: 'Landshypotek',
            type: 1,
            comment: 2,
            regex: /^939[0-9]{8}$/,
        },
        {
            name: 'Lån & Spar Bank Sverige',
            type: 1,
            comment: 1,
            regex: /^963[0-9]{8}$/,
        },
        {
            name: 'Länsförsäkringar Bank',
            type: 1,
            comment: 1,
            regex: /^(340|906)[0-9]{8}$/,
        },
        {
            name: 'Länsförsäkringar Bank',
            type: 1,
            comment: 2,
            regex: /^902[0-9]{8}$/,
        },
        {
            name: 'Marginalen Bank',
            type: 1,
            comment: 1,
            regex: /^923[0-9]{8}$/,
        },
        {
            name: 'MedMera Bank',
            type: 1,
            comment: 2,
            regex: /^965[0-9]{8}$/,
        },
        {
            name: 'Nordax Bank',
            type: 1,
            comment: 2,
            regex: /^964[0-9]{8}$/,
        },
        {
            name: 'Nordea',
            type: 1,
            comment: 1,
            regex: /^(?!3300|3782)(1[1456789][0-9]{2}|20[0-9]{2}|3[0-3][0-9]{2}|34[1-9][0-9]|3[5-9][0-9]{2})[0-9]{7}$/,
        },
        {
            name: 'Nordea',
            type: 1,
            comment: 2,
            regex: /^4[0-9]{10}$/,
        },
        {
            name: 'Nordnet Bank',
            type: 1,
            comment: 2,
            regex: /^910[0-9]{8}$/,
        },
        {
            name: 'Northmill Bank AB',
            type: 1,
            comment: 2,
            regex: /^975[0-9]{8}$/,
        },
        {
            name: 'Resurs Bank',
            type: 1,
            comment: 1,
            regex: /^928[0-9]{8}$/,
        },
        {
            name: 'Riksgälden',
            type: 1,
            comment: 2,
            regex: /^988[0-9]{8}$/,
        },
        {
            name: 'Santander Consumer Bank',
            type: 1,
            comment: 1,
            regex: /^946[0-9]{8}$/,
        },
        {
            name: 'SBAB',
            type: 1,
            comment: 1,
            regex: /^925[0-9]{8}$/,
        },
        {
            name: 'SEB',
            type: 1,
            comment: 1,
            regex: /^(5[0-9]{3}|912[0-4]|91[3-4][0-9])[0-9]{7}$/,
        },
        {
            name: 'Skandiabanken',
            type: 1,
            comment: 2,
            regex: /^91[5-6][0-9]{8}$/,
        },
        {
            name: 'Swedbank',
            type: 1,
            comment: 1,
            regex: /^7[0-9]{10}$/,
        },
        {
            name: 'Ålandsbanken',
            type: 1,
            comment: 2,
            regex: /^23[0-9]{9}$/,
        },
        // TYP 2
        {
            name: 'Danske Bank',
            type: 2,
            comment: 1,
            regex: /^918[0-9]{11}$/,
        },
        {
            name: 'Handelsbanken',
            type: 2,
            comment: 2,
            regex: /^6[0-9]{12}$/,
        },
        {
            name: 'Nordera Plusgirot',
            type: 2,
            comment: 3,
            regex: /^(95[0-4]|996)[0-9]{8,11}$/, // Clearingnr 4 siffror, räkningsnr 7-10 siffror
        },
        {
            name: 'Nordea',
            type: 2,
            comment: 1,
            regex: /^(3300|3782)[0-9]{10}$/,
        },
        {
            name: 'Riksgälden',
            type: 2,
            comment: 1,
            regex: /^989[0-9]{11}$/,
        },
        {
            name: 'Sparbanken Syd',
            type: 2,
            comment: 1,
            regex: /^957[0-9]{11}$/,
        },
        {
            name: 'Swedbank',
            type: 2,
            comment: 3,
            regex: /^8[0-9]{10,14}$/, // Clearingnr 5 siffror, räkningsnr 7-10 siffror
        },
        {
            name: 'Swedbank',
            type: 2,
            comment: 1,
            regex: /^93[0-4][0-9]{11}$/,
        },
    ];

    for (const i in banks) {
        const bank = banks[i];
        if (bank.regex.test(n)) {
            return validate(n, bank);
        }
    }
    return false;
}

/**
 * Stödfunktion för att validera kontonummer.
 *
 * @param {String} accnum Bankkontonummer
 * @param {Object} bank Bankdata
 * @returns {Object|Boolean}
 */
const validate = function (accnum: string, bank: Bank) {
    const clearing = accnum.substr(0, accnum.charAt(0) === '8' ? 5 : 4);

    const number =
        // Samtliga konton av typ 1
        bank.type === 1
            ? accnum.substr(-7)
            : // Handelsbankens koton av typ 2
            bank.type === 2 && bank.comment === 2
            ? accnum.substr(-9)
            : // Swedbanks konton av typ 2
            bank.type === 2 && bank.comment === 3 && accnum.charAt(0) === '8'
            ? accnum.substr(5)
            : // Plusgirots konton av typ 2
            bank.type === 2 && bank.comment === 3 && accnum.charAt(0) === '9'
            ? accnum.substr(4)
            : // Resterande konton av typ 2
              accnum.substr(-10);

    const modResult =
        bank.type === 1 && bank.comment === 1
            ? mod11((clearing + number).substr(-10))
            : bank.type === 1 && bank.comment === 2
            ? mod11(clearing + number)
            : bank.type === 2 && bank.comment === 2
            ? mod11(number)
            : mod10(number);

    return !modResult
        ? false
        : {
              bankName: bank.name,
              clearingNumber: clearing,
              accountNumber: number,
          };
};

/**
 * Stödfunktion för att kontrollera mod10.
 *
 * @param {String} number Bankkontonummer
 * @returns {Boolean}
 */
const mod10 = function (number: string) {
    let len = number.length;
    let bit = 1;
    let sum = 0;
    const arr = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];
    while (len) {
        const val = parseInt(number.charAt(--len), 10);
        // eslint-disable-next-line no-cond-assign
        sum += (bit ^= 1) ? arr[val] : val;
    }
    return sum && sum % 10 === 0;
};

/**
 * Stödfunktion för att kontrollera mod11.
 *
 * @param {String} number Bankkontonummer
 * @returns {Boolean}
 */
const mod11 = function (number: string) {
    let len = number.length;
    let sum = 0;
    const weights = [1, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
    const arr = weights.splice(weights.length - len, weights.length - (weights.length - len));
    while (len) {
        const val = parseInt(number.charAt(--len), 10);
        sum += arr[len] * val;
    }
    return sum && sum % 11 === 0;
};

type AccountNumberInfo = { isValid: boolean; type?: 'BankGiro' | 'PlusGiro' | 'Normal'; formatted: string };

const BANKGIROT_RE = /^(\d{3,4})-?(\d{4})$/;
const PLUSGIROT_RE = /^(\d{1,7})-(\d$)/;

export function parseSwedishAccountNumber(value: string, scope: ValidationScope = 'all'): AccountNumberInfo {
    const cleaned = value.replace(/[^a-zA-Z0-9_-]/gi, '');

    let m = BANKGIROT_RE.exec(cleaned);
    if (scope == 'all') {
        if (m) {
            return {
                isValid: true,
                type: 'BankGiro',
                formatted: `${m[1]}-${m[2]}`,
            };
        }
        if ((m = PLUSGIROT_RE.exec(cleaned))) {
            return {
                isValid: true,
                type: 'PlusGiro',
                formatted: `${m[1]}-${m[2]}`,
            };
        }
    }
    const validation = validateSwedishAccountNumber(cleaned);
    if (validation !== false) {
        return {
            isValid: true,
            type: 'Normal',
            formatted: `${validation.clearingNumber}-${validation.accountNumber}`,
        };
    }

    return {
        isValid: false,
        formatted: value,
    };
}
